import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ReceivedProductsColumns } from './products/received-products-columns-constants';
import { unspecifiedWarehouseLocation } from '../../../../../constants/unspecified-warehouse-location';
import { useLuxonUserDateFormat } from '../../../../../hooks/use-datetime-formats';
import { useSimpleToggle } from '../../../../../hooks/use-simple-toggle';
import { getDateAsUtcNoonInISO } from '../../../../../services/datetime-service';
import { LogService } from '../../../../../services/log-service';
import { getPurchaseOrdersReceives, postPurchaseOrdersReceives } from '../../api/purchase-order-api';

/**
 * @typedef {object} ReceivedProductsContextType
 * @property {Array<ReceiveFormRow>} receiveFormData
 * @property {Function} updateData
 * @property {{ received: number, ordered: number, rejected: number }} totals
 * @property {number} dynamicReceived
 * @property {number} dynamicRejected
 * @property {Function} addScannedProduct
 * @property {Function} handleSubmitData
 * @property {boolean} isLoading
 * @property {boolean} showAdvancedColumns
 * @property {Function} toggleAdvancedColumns
 * @property {number|null} highlightedProductId
 * @property {boolean} isFormTouched
 * @property {boolean} isSaving
 */

/**
 * @typedef {import('../../api/purchase-order-api').PurchaseOrderReceive} PurchaseOrderReceive
 * @typedef {import('../../receive-purchase-order-drawer/use-warehouse-locations').WarehouseLocation} WarehouseLocation
 */

/**
 * @typedef {PurchaseOrderReceive & {
 *   accept: number,
 *   reject: number,
 *   upc: string|null,
 *   expirationDate?: string,
 *   lot?: string,
 * }} ReceiveFormRow
 */

const ReceivedProductsContext = createContext(/** @type {ReceivedProductsContextType|null} */ (null));

/**
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {number} props.purchaseOrderId - The purchase order ID
 * @param {Array<WarehouseLocation>} [props.warehouseLocations] - Warehouse locations
 * @returns {JSX.Element} The provider component
 */
export const ReceivedProductsProvider = ({ children, purchaseOrderId, warehouseLocations = [] }) => {
  /** @type {[Array<ReceiveFormRow>, Function]} */
  const [receiveFormData, setReceiveFormData] = useState([]);
  const [initialFormData, setInitialFormData] = useState([]);
  const [dynamicReceived, setDynamicReceived] = useState(0);
  const [dynamicRejected, setDynamicRejected] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [highlightedProductId, setHighlightedProductId] = useState(null);
  const userDateFormat = useLuxonUserDateFormat();
  const [showAdvancedColumns, toggleAdvancedColumns] = useSimpleToggle();
  const [isSaving, setIsSaving] = useState(false);

  const receiveFormDataRef = useRef(receiveFormData);
  receiveFormDataRef.current = receiveFormData;

  const warehouseLocationsRef = useRef(warehouseLocations);
  warehouseLocationsRef.current = warehouseLocations;

  const isFormTouched = useMemo(() => {
    return receiveFormData.some((item, index) => {
      const original = initialFormData[index];
      return item.accept !== original?.accept || item.reject !== original?.reject;
    });
  }, [receiveFormData, initialFormData]);

  const aggregateData = (initialData) => {
    return initialData.reduce((acc, item) => {
      const existingProduct =
        item.purchaseOrderProductId != null
          ? acc.find((product) => product.purchaseOrderProductId === item.purchaseOrderProductId)
          : undefined;

      if (existingProduct) {
        existingProduct.receivedQuantity += item.receivedQuantity;
        existingProduct.rejectedQuantity += item.rejectedQuantity;
      } else {
        acc.push({
          ...item,
          inventoryLocationId: item.lastLocationId ?? 0,
          accept: 0,
          reject: 0,
          inventoryLocationIdError: false,
        });
      }

      return acc;
    }, []);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseData = await getPurchaseOrdersReceives(purchaseOrderId);
        const result = aggregateData(responseData);
        setReceiveFormData(result);
        setInitialFormData(result);

        const initialReceived = result.reduce((acc, item) => acc + (item.receivedQuantity || 0), 0);
        const initialRejected = result.reduce((acc, item) => acc + (item.rejectedQuantity || 0), 0);
        setDynamicReceived(initialReceived);
        setDynamicRejected(initialRejected);
        setIsLoading(false);
      } catch (error) {
        LogService.logError(new Error('Error fetching Purchase Order Receive form history', { cause: error }));
      }
    };

    if (purchaseOrderId) {
      fetchData();
    }
  }, [purchaseOrderId]);

  const totals = useMemo(() => {
    return receiveFormData.reduce(
      (acc, item) => ({
        received: acc.received + (item.receivedQuantity || 0),
        ordered: acc.ordered + (item.orderedQuantity || 0),
        rejected: acc.rejected + (item.rejectedQuantity || 0),
      }),
      { received: 0, ordered: 0, rejected: 0 },
    );
  }, [receiveFormData]);

  const updateData = useCallback(
    (rowIndex, columnId, value) => {
      setReceiveFormData((oldData) => {
        const prevRow = oldData[rowIndex];
        const currentValue = prevRow[columnId];

        if (String(currentValue) === String(value)) return oldData;

        const prevValue = Number(currentValue) || 0;
        const newValue = Number(value);
        const delta = newValue - prevValue;

        if (columnId === ReceivedProductsColumns.ACCEPT) {
          setDynamicReceived((prev) => prev + delta);
        } else if (columnId === ReceivedProductsColumns.REJECT) {
          setDynamicRejected((prev) => prev + delta);
        }

        const parsedValue = columnId === 'expirationDate' ? getDateAsUtcNoonInISO(value, userDateFormat) : value;

        const updated = [...oldData];
        updated[rowIndex] = { ...prevRow, [columnId]: parsedValue };
        return updated;
      });
    },
    [userDateFormat],
  );

  const addScannedProduct = useCallback((scannedProduct) => {
    const quantityDelta = scannedProduct._quantityDelta ?? 1;
    setReceiveFormData((currentData) => {
      const updatedData = [...currentData];

      let indexToUpdate;
      if (scannedProduct.purchaseOrderProductId != null) {
        indexToUpdate = updatedData.findIndex((p) => p.purchaseOrderProductId === scannedProduct.purchaseOrderProductId);
      } else {
        const skuMatches = updatedData.reduce((acc, p, i) => {
          if (p.sku === scannedProduct.sku) acc.push(i);
          return acc;
        }, []);
        const firstSkuWithCapacity = skuMatches.find(
          (i) => (updatedData[i].receivedQuantity || 0) + (updatedData[i].accept || 0) < (updatedData[i].orderedQuantity || 0),
        );
        indexToUpdate = firstSkuWithCapacity ?? skuMatches[0] ?? -1;
      }

      if (indexToUpdate === -1 && updatedData.length > 0) {
        const firstWithCapacity = updatedData.findIndex((p) => (p.receivedQuantity || 0) + (p.accept || 0) < (p.orderedQuantity || 0));
        indexToUpdate = firstWithCapacity !== -1 ? firstWithCapacity : 0;
      }

      if (indexToUpdate !== -1) {
        const updatedProduct = {
          ...updatedData[indexToUpdate],
          accept: (updatedData[indexToUpdate].accept || 0) + quantityDelta,
        };

        const currentWarehouseLocations = warehouseLocationsRef.current;
        if (updatedProduct.inventoryLocationId === 0 && currentWarehouseLocations?.length) {
          const unspecifiedLocation = currentWarehouseLocations.find((loc) => loc?.area === unspecifiedWarehouseLocation);
          if (unspecifiedLocation?.inventoryLocationId) {
            updatedProduct.inventoryLocationId = unspecifiedLocation.inventoryLocationId;
          }
        }
        updatedData[indexToUpdate] = updatedProduct;
        setDynamicReceived((prev) => prev + quantityDelta);
      }
      setHighlightedProductId(scannedProduct.productId);

      return updatedData;
    });
  }, []);

  const getTouchedRowsData = useCallback((inventoryWarehouseId) => {
    const currentFormData = receiveFormDataRef.current;
    const productReceives = currentFormData
      .filter(({ accept, reject }) => accept || reject)
      .map(({ purchaseOrderProductId, accept, reject, rejectReason, inventoryLocationId, expirationDate, lot, productId, sku }) => ({
        purchaseOrderProductId,
        receivedQuantity: accept,
        rejectedQuantity: reject,
        rejectReason,
        inventoryLocationId,
        expirationDate,
        lot,
        productId,
        sku,
        // if there are more required fields in the future we should agregate errors into one property formFieldErrors and do not send it to BE anymore
        inventoryLocationIdError: !inventoryLocationId,
      }));

    return {
      inventoryWarehouseId,
      productReceives,
    };
  }, []);

  const handleSubmitData = useCallback(
    async (inventoryWarehouseId) => {
      const { inventoryWarehouseId: id, productReceives } = getTouchedRowsData(inventoryWarehouseId);
      if (!productReceives.length) return;

      const touchedBySku = new Map(productReceives.map((item) => [item.sku, item]));

      const hasErrors = productReceives.some((item) => {
        return item.inventoryLocationIdError;
      });

      if (hasErrors) {
        setReceiveFormData((prev) =>
          prev.map((row) => {
            const touched = touchedBySku.get(row.sku);
            if (!touched || !touched.inventoryLocationIdError) return row;

            return {
              ...row,
              inventoryLocationIdError: true,
            };
          }),
        );
        return;
      }

      const apiPayload = {
        inventoryWarehouseId: id,
        productReceives,
      };

      try {
        setIsSaving(true);
        await postPurchaseOrdersReceives(purchaseOrderId, apiPayload);
        return true;
      } catch (error) {
        LogService.logError(new Error('Error submitting Purchase Order Receive form', { cause: error }));
      } finally {
        setIsSaving(false);
      }
      return false;
    },
    [getTouchedRowsData, purchaseOrderId],
  );

  const value = useMemo(
    () => ({
      receiveFormData,
      updateData,
      totals,
      dynamicReceived,
      dynamicRejected,
      addScannedProduct,
      handleSubmitData,
      isLoading,
      showAdvancedColumns,
      toggleAdvancedColumns,
      highlightedProductId,
      isFormTouched,
      isSaving,
    }),
    [
      receiveFormData,
      updateData,
      totals,
      dynamicReceived,
      dynamicRejected,
      addScannedProduct,
      handleSubmitData,
      isLoading,
      showAdvancedColumns,
      toggleAdvancedColumns,
      highlightedProductId,
      isFormTouched,
      isSaving,
    ],
  );

  return <ReceivedProductsContext.Provider value={value}>{children}</ReceivedProductsContext.Provider>;
};

export const useReceivedProducts = () => useContext(ReceivedProductsContext);
